import React, { useEffect, useMemo, useState } from "react";
import { getProductReviewsPublic, toAbsUrl } from "../utils/api";
import StarRating from "./StarRating"; // ✅ NOW EXISTS

/* =========================
   Helpers
   ========================= */

function normalizeReviewResponse(res) {
  const count = Number(res?.count ?? res?.data?.count ?? 0) || 0;
  const avg = Number(res?.avg ?? res?.data?.avg ?? 0) || 0;

  const reviews =
    (Array.isArray(res?.reviews) && res.reviews) ||
    (Array.isArray(res?.data?.reviews) && res.data.reviews) ||
    (Array.isArray(res?.items) && res.items) ||
    (Array.isArray(res) && res) ||
    [];

  return { count, avg, reviews };
}

function isVideoType(x) {
  const t = String(x?.type || x || "").toLowerCase();
  return t === "video" || t.startsWith("video/");
}

export default function ProductReviews({ productId, defaultOpen = false, theme }) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [data, setData] = useState({ count: 0, avg: 0, reviews: [] });

  async function load() {
    if (!productId) return;
    setLoading(true);
    setErr("");
    try {
      const res = await getProductReviewsPublic(productId); // ✅ public
      setData(normalizeReviewResponse(res));
    } catch (e) {
      setErr(e?.message || "Failed to load reviews");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [productId]);

  const avgText = useMemo(() => Number(data.avg || 0).toFixed(1), [data.avg]);

  // Use theme for styling if provided, otherwise fall back to default styles
  const styles = theme ? getThemedStyles(theme) : S;

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <div style={S.title}>Reviews</div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <StarRating value={data.avg} size={16} />
            <span style={S.sub}>
              {avgText} / 5 • {data.count} review{data.count === 1 ? "" : "s"}
            </span>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{...styles.btn, ...(open ? styles.btnActive : {})}}
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>

      {open && (
        <>
          {loading && <div style={styles.note}>Loading reviews…</div>}
          {!loading && err && <div style={styles.errBox}>{err}</div>}

          {!loading && !err && data.reviews.length === 0 && (
            <div style={styles.note}>No reviews yet.</div>
          )}

          {!loading && !err && data.reviews.length > 0 && (
            <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
              {data.reviews.map((r) => (
                <div key={r._id || r.id} style={styles.reviewCard}>
                  <div style={styles.reviewTop}>
                    <div>
                      <div style={styles.reviewName}>
                        {r.userName || r.user?.name || "User"}
                      </div>
                      <StarRating value={r.rating} size={14} />
                    </div>

                    <div style={styles.reviewTime}>
                      {r.createdAt
                        ? new Date(r.createdAt).toLocaleDateString()
                        : ""}
                    </div>
                  </div>

                  {r.comment && (
                    <div style={styles.reviewText}>{r.comment}</div>
                  )}

                  {Array.isArray(r.media) && r.media.length > 0 && (
                    <div style={styles.mediaGrid}>
                      {r.media.map((m, idx) => {
                        const url = toAbsUrl(m?.url || m);
                        const isVideo = isVideoType(m);
                        return (
                          <div key={idx} style={styles.mediaItem}>
                            {isVideo ? (
                              <video src={url} controls style={styles.mediaThumb} />
                            ) : (
                              <img src={url} alt="" style={styles.mediaThumb} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* =========================
   Themed Styles Function
   ========================= */
function getThemedStyles(theme) {
  return {
    wrap: {
      marginTop: 14,
      borderRadius: 12,
      border: `1px solid ${theme.border}`,
      background: theme.card,
      padding: 20,
      color: theme.text,
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
    },
    title: { fontWeight: 700, fontSize: 18 },
    sub: { fontSize: 14, fontWeight: 500, color: theme.muted },
    btn: {
      padding: "10px 16px",
      borderRadius: 8,
      border: `1px solid ${theme.border}`,
      background: theme.bg,
      color: theme.text,
      cursor: "pointer",
      fontWeight: 600,
      fontSize: 14,
    },
    btnActive: {
      borderColor: theme.primary,
      background: `${theme.primary}20`,
      color: theme.primary,
    },
    note: {
      marginTop: 16,
      padding: 16,
      borderRadius: 8,
      border: `1px dashed ${theme.border}`,
      color: theme.muted,
      textAlign: "center",
      fontWeight: 500,
    },
    errBox: {
      marginTop: 16,
      padding: 16,
      borderRadius: 8,
      background: "rgba(239, 68, 68, 0.1)",
      border: "1px solid rgba(239, 68, 68, 0.3)",
      color: "#ef4444",
      fontWeight: 500,
    },
    reviewCard: {
      borderRadius: 8,
      border: `1px solid ${theme.border}`,
      background: theme.bg,
      padding: 16,
    },
    reviewTop: {
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
      alignItems: "flex-start",
    },
    reviewName: { fontWeight: 600, fontSize: 14 },
    reviewTime: { fontSize: 12, color: theme.muted },
    reviewText: { marginTop: 12, color: theme.text, fontWeight: 400, lineHeight: 1.5 },
    mediaGrid: {
      marginTop: 12,
      display: "grid",
      gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
      gap: 8,
    },
    mediaItem: {
      borderRadius: 8,
      overflow: "hidden",
      height: 100,
    },
    mediaThumb: { width: "100%", height: "100%", objectFit: "cover" },
  };
}

/* =========================
   Default Styles (fallback)
   ========================= */
const S = {
  wrap: {
    marginTop: 14,
    borderRadius: 18,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(15,23,42,0.55)",
    padding: 14,
    color: "#fff",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  title: { fontWeight: 950, fontSize: 16 },
  sub: { fontSize: 12, fontWeight: 900, opacity: 0.85 },
  btn: {
    padding: "8px 12px",
    borderRadius: 12,
    borderWidth: "1px",
    borderStyle: "solid",
    borderColor: "rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "#fff",
    cursor: "pointer",
    fontWeight: 900,
  },
  btnActive: {
    borderColor: "rgba(59,130,246,0.5)",
    background: "rgba(59,130,246,0.18)",
  },
  note: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    border: "1px dashed rgba(255,255,255,0.18)",
    fontWeight: 900,
  },
  errBox: {
    marginTop: 12,
    padding: 12,
    borderRadius: 14,
    background: "rgba(239,68,68,0.15)",
    fontWeight: 900,
  },
  reviewCard: {
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.04)",
    padding: 12,
  },
  reviewTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
  },
  reviewName: { fontWeight: 950 },
  reviewTime: { fontSize: 12, opacity: 0.6 },
  reviewText: { marginTop: 8, opacity: 0.9, fontWeight: 800 },
  mediaGrid: {
    marginTop: 10,
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
    gap: 10,
  },
  mediaItem: {
    borderRadius: 14,
    overflow: "hidden",
    height: 110,
  },
  mediaThumb: { width: "100%", height: "100%", objectFit: "cover" },
};
